import { useState } from 'react';
import {
  Box, Typography, Button, Popover, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Divider, alpha, useTheme, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import { AccountBalanceWallet, Stars, Sync, Edit } from '@mui/icons-material';
import { chargeFieldLabel, formatMoney } from '../../utils/currency';

export type AdjustMode = 'credit' | 'debit';

const REASON_MIN = 5;
const MAX_WALLET_ADJUSTMENT = 100000;

interface BalanceAdjustPopoverProps {
  type: 'wallet' | 'loyalty';
  displayValue: string;
  /** Numeric balance — enables new-balance preview and debit limit. */
  currentBalance?: number;
  disabled?: boolean;
  disabledReason?: string;
  loading?: boolean;
  onRefresh?: () => void;
  /** May return a promise; the dialog stays open until it settles and shows any error inline. */
  onApply: (mode: AdjustMode, amount: number, note: string) => unknown | Promise<unknown>;
  lastTxn?: { amount: number; description?: string; createdAt: string; adjustedByName?: string | null } | null;
  onViewHistory?: () => void;
}

const QUICK_AMOUNTS = {
  wallet: [100, 500, 1000, 5000],
  loyalty: [25, 50, 100, 250],
};

function amountError(raw: string, isWallet: boolean, mode: AdjustMode, balance?: number): string | undefined {
  if (!raw.trim()) return 'Enter an amount';
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 'Amount must be greater than 0';
  if (isWallet && !/^\d+(\.\d{1,2})?$/.test(raw.trim())) return 'Use at most 2 decimal places';
  if (!isWallet && !Number.isInteger(n)) return 'Points must be a whole number';
  if (isWallet && n > MAX_WALLET_ADJUSTMENT) return `A single adjustment cannot exceed ${formatMoney(MAX_WALLET_ADJUSTMENT)}`;
  if (mode === 'debit' && balance != null && n > balance) return `Cannot debit more than the current balance (${isWallet ? formatMoney(balance) : balance})`;
  return undefined;
}

export default function BalanceAdjustPopover({
  type, displayValue, currentBalance, disabled, disabledReason, loading, onRefresh, onApply, lastTxn, onViewHistory,
}: BalanceAdjustPopoverProps) {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [mode, setMode] = useState<AdjustMode>('credit');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState('');

  const open = Boolean(anchor);
  const isWallet = type === 'wallet';
  const Icon = isWallet ? AccountBalanceWallet : Stars;
  const accent = isWallet ? theme.palette.primary.main : theme.palette.secondary.main;
  const fmt = (n: number) => (isWallet ? formatMoney(n) : `${n} pts`);

  const amtErr = amountError(amount, isWallet, mode, currentBalance);
  const reasonErr = note.trim().length < REASON_MIN ? `Reason is required (at least ${REASON_MIN} characters)` : undefined;
  const canApply = !amtErr && !reasonErr && !loading;
  const num = Number(amount) || 0;
  const newBalance = currentBalance != null ? currentBalance + (mode === 'credit' ? num : -num) : undefined;

  const reset = () => {
    setAmount('');
    setNote('');
    setMode('credit');
    setTouched(false);
    setSubmitError('');
  };

  const handleClose = () => {
    if (submitting) return;
    setAnchor(null);
    reset();
  };

  const handleApply = () => {
    setTouched(true);
    if (!canApply) return;
    setSubmitError('');
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!canApply) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await onApply(mode, num, note.trim());
      setConfirmOpen(false);
      setAnchor(null);
      setSuccess(`${fmt(num)} ${mode === 'credit' ? 'credited' : 'debited'}${newBalance != null ? ` — new balance ${fmt(newBalance)}` : ''}`);
      reset();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err instanceof Error ? err.message : 'Adjustment failed');
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box
        onClick={(e) => { if (!disabled) { e.stopPropagation(); setAnchor(e.currentTarget); } }}
        role={disabled ? undefined : 'button'}
        aria-label={disabled ? undefined : `Adjust ${type}`}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          px: 1.25,
          py: 0.75,
          borderRadius: 1.5,
          border: 1,
          borderColor: disabled ? 'divider' : alpha(accent, 0.35),
          bgcolor: disabled ? 'action.hover' : alpha(accent, 0.06),
          minWidth: 120,
          transition: 'border-color 0.2s, box-shadow 0.2s',
          '&:hover': disabled ? {} : {
            borderColor: accent,
            boxShadow: `0 0 0 1px ${alpha(accent, 0.2)}`,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Icon sx={{ fontSize: 16, color: accent, opacity: 0.9 }} />
          <Typography variant="body2" fontWeight={700} noWrap>
            {displayValue}
          </Typography>
        </Stack>
        {disabled ? (
          disabledReason && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
              {disabledReason}
            </Typography>
          )
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ mt: 0.25 }}>
            <Edit sx={{ fontSize: 12, color: accent }} />
            <Typography variant="caption" color={accent} fontWeight={600}>
              Credit / debit
            </Typography>
          </Stack>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 300, borderRadius: 2 } } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            {isWallet ? 'Wallet adjustment' : 'Loyalty adjustment'}
          </Typography>
          {onRefresh && (
            <Button size="small" startIcon={<Sync fontSize="small" />} onClick={onRefresh} disabled={loading}>
              Sync
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Current balance: <strong>{displayValue}</strong>
        </Typography>

        <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, bgcolor: 'action.hover' }}>
          <Typography variant="caption" fontWeight={700} display="block">Audit trail</Typography>
          {lastTxn ? (
            <Typography variant="caption" color="text.secondary" display="block">
              Last change: {lastTxn.amount >= 0 ? '+' : '−'}{fmt(Math.abs(lastTxn.amount))}
              {' on '}{new Date(lastTxn.createdAt).toLocaleString()}
              {lastTxn.adjustedByName ? ` by ${lastTxn.adjustedByName}` : ''}
              {lastTxn.description ? ` — ${lastTxn.description}` : ''}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" display="block">No previous transactions.</Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            Every adjustment needs a reason and confirmation, and is logged with your name and time.
          </Typography>
          {onViewHistory && (
            <Button size="small" sx={{ px: 0, minWidth: 0, mt: 0.25 }} onClick={() => { handleClose(); onViewHistory(); }}>
              View full history
            </Button>
          )}
        </Box>

        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="credit" color="success">Credit (+)</ToggleButton>
          <ToggleButton value="debit" color="warning">Debit (−)</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          autoFocus
          fullWidth
          size="small"
          required
          label={isWallet ? chargeFieldLabel('Amount') : 'Points'}
          value={amount}
          inputProps={{ inputMode: 'decimal' }}
          onChange={(e) => {
            const v = e.target.value.replace(/[^\d.]/g, '');
            if (isWallet ? /^\d{0,6}(\.\d{0,2})?$/.test(v) : /^\d{0,6}$/.test(v)) setAmount(v);
          }}
          onBlur={() => setTouched(true)}
          error={touched && !!amtErr}
          helperText={(touched && amtErr)
            || (newBalance != null && num > 0 ? `New balance will be ${fmt(newBalance)}` : `Max ${isWallet ? formatMoney(MAX_WALLET_ADJUSTMENT) : '999999 pts'} per adjustment`)}
          sx={{ mb: 1.5 }}
        />

        <Stack direction="row" flexWrap="wrap" gap={0.75} mb={1.5}>
          {QUICK_AMOUNTS[type].map((q) => (
            <Button
              key={q}
              size="small"
              variant={Number(amount) === q ? 'contained' : 'outlined'}
              onClick={() => setAmount(String(q))}
              sx={{ minWidth: 0, px: 1.25, py: 0.25, fontSize: 12 }}
            >
              {isWallet ? `${formatMoney(q)}` : q}
            </Button>
          ))}
        </Stack>

        <TextField
          fullWidth
          size="small"
          required
          label="Reason"
          placeholder="e.g. Incentive for weekend jobs"
          value={note}
          inputProps={{ maxLength: 200 }}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && !!reasonErr}
          helperText={(touched && reasonErr) || 'Saved in wallet history with your name and the time'}
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ mb: 1.5 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={handleClose}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            color={mode === 'credit' ? 'success' : 'warning'}
            disabled={loading}
            onClick={handleApply}
          >
            Review {mode === 'credit' ? 'credit' : 'debit'}
          </Button>
        </Stack>
      </Popover>

      <Dialog
        open={confirmOpen}
        onClose={() => { if (!submitting) setConfirmOpen(false); }}
        onClick={(e) => e.stopPropagation()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm {isWallet ? 'wallet' : 'loyalty'} {mode}</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 1.5 }}>{submitError}</Alert>}
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary' }}>Current balance</TableCell>
                <TableCell align="right">{displayValue}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary' }}>{mode === 'credit' ? 'Credit' : 'Debit'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: mode === 'credit' ? 'success.main' : 'warning.main' }}>
                  {mode === 'credit' ? '+' : '−'}{fmt(num)}
                </TableCell>
              </TableRow>
              {newBalance != null && (
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>New balance</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(newBalance)}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', borderBottom: 0 }}>Reason</TableCell>
                <TableCell align="right" sx={{ borderBottom: 0, wordBreak: 'break-word' }}>{note.trim()}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            This is recorded in the wallet history as an admin adjustment with your name and timestamp, and cannot be undone automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Back</Button>
          <Button
            variant="contained"
            color={mode === 'credit' ? 'success' : 'warning'}
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {submitting ? 'Applying…' : 'Confirm & apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={5000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </>
  );
}
